import { useEffect, useRef, useState, useMemo } from 'react';

import { Checkbox } from '@raptormaps/checkbox';
import { Table } from '@raptormaps/table';
import { Button } from '@raptormaps/button';
import { Stack } from '@raptormaps/layout';
import { CircularProgress } from '@raptormaps/progress';
import { AlertModal } from '@raptormaps/alert-modal';

import {
  useGlobalUserRoles,
  useUpdateGlobalUserRoles,
} from '../../hooks/useRoles';
import type { OrgUser } from '../../api/types/OrgUser';
import type { Role } from '../../api/schemas/UserPermissionsSchema';
import { RAPTOR_ADMIN_ROLE } from './constants';

const tableColumns = [
  {
    name: 'Email',
    key: 'email',
  },
  {
    name: 'Raptor Admin V2',
    key: 'raptoradmin',
  },
];

interface NewRolesProps {
  users: OrgUser[];
  changedUsers: OrgUser[];
  setChangedUsers: (users: OrgUser[]) => void;
}

export const NewRoles = ({
  users,
  changedUsers,
  setChangedUsers,
}: NewRolesProps) => {
  const [combinedUsers, setCombinedUsers] = useState<OrgUser[]>([]);
  const triggerAlertModalRef = useRef<() => void>(() => null);
  const userIds = useMemo(() => users.map(u => u.auth0UserId), [users]);
  const { data: fgaUsers, isLoading } = useGlobalUserRoles(userIds);
  const { mutateAsync: updateGlobalUserRoles, isLoading: isSaving } =
    useUpdateGlobalUserRoles();

  useEffect(() => {
    if (!fgaUsers || !users.length) return;

    const updatedUsers = users.map(user => {
      const fgaUser = fgaUsers?.find(fga => fga.user_id === user.auth0UserId);
      const isRaptorAdmin: boolean =
        fgaUser?.object_permissions.roles?.some(
          (role: Role) => role.role_id === RAPTOR_ADMIN_ROLE,
        ) ?? false;

      return {
        ...user,
        raptoradmin: isRaptorAdmin,
      };
    });
    setCombinedUsers(updatedUsers);
  }, [users, fgaUsers]);

  const handleCheckboxChange = (user: OrgUser, isChecked: boolean) => {
    const updatedUser = {
      ...user,
      raptoradmin: isChecked,
    };
    // if user is not in the changedUsers array, add them
    if (!changedUsers.some(u => u.email === user.email)) {
      setChangedUsers([...changedUsers, updatedUser]);
    } else {
      // if user is already in the changedUsers array, update them
      const index = changedUsers.findIndex(u => u.email === user.email);
      changedUsers[index] = updatedUser;
    }

    const combinedIndex = combinedUsers.findIndex(u => u.email === user.email);
    if (combinedIndex !== -1) {
      combinedUsers[combinedIndex] = updatedUser;
    }
    setCombinedUsers([...combinedUsers]);
  };

  const formatData = combinedUsers.map(user => ({
    email: user.email,
    raptoradmin: (
      <Checkbox
        checked={user.raptoradmin}
        size="small"
        style={{ marginLeft: 4 }}
        onCheckedChange={(isChecked: boolean) =>
          handleCheckboxChange(user, isChecked)
        }
      />
    ),
  }));

  return (
    <>
      {isLoading || !users.length ? (
        <Stack align="center" style={{ padding: 24 }}>
          <CircularProgress size={75} />
        </Stack>
      ) : (
        <Stack style={{ paddingBottom: 24 }}>
          <Table
            cols={tableColumns}
            data={formatData}
            fetchData={() => Promise.resolve()}
            totalRowCount={1}
            hideControls
          />

          <Button
            disabled={!changedUsers.length}
            style={{ alignSelf: 'start', width: 300 }}
            onClick={() => {
              triggerAlertModalRef.current();
            }}
          >
            {changedUsers.length
              ? 'Save Roles'
              : isSaving
              ? 'Saving...'
              : 'Saved'}
          </Button>

          <AlertModal
            title="Are you sure?"
            description="Saving will change the Raptor Admin role of the selected users. Raptor Admins have full administrative privileges, including the ability to manage settings, users, and permissions across all organizations in Raptor Maps."
            triggerRef={triggerAlertModalRef}
            onConfirm={() => {
              updateGlobalUserRoles(changedUsers);
              setChangedUsers([]);
            }}
            confirmBtnText="Save"
          />
        </Stack>
      )}
    </>
  );
};
